<template>
  <div class="step-container client">
    <help-drawer :title="helpTitle">
      <div>
        <p>
          You may want to provide an additional phone number as a secondary form
          of communication (home, work, other)
        </p>
      </div>
    </help-drawer>
    <v-form name="AlternatePhone" class="form" data-test-id="alternate-phone-form">
      <v-container class="client-step-container">
        <syncing-indicator :pageName="MENUS.CLIENT.ALTERNATE_PHONE.id"></syncing-indicator>
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1 data-test-id="alternate-phone-header">
              <span class="headerlight">
                Would you like to add another Mobile Phone?
              </span>
            </h1>
            <div class="help-link">
              <a @click="toggleHelp()" data-test-id="help-link">{{ helpTitle }}</a>
            </div>
          </v-col>
        </v-row>
        <data-submitted-message
          :submitted="isAlternatePhoneComplete"
          v-on:hide-form="toggleFormState"
          data-test-id="data-submitted-message"
        />
        <div v-if="!formHidden && !isSyncing">
          <v-row class="justify-center">
            <v-col class="step-form-container radio-container mb-4">
              <v-radio-group
                v-model="hasAlternatePhone"
                :error-messages="alternatePhoneErrors"
                hide-details="auto"
                @change="$v.hasAlternatePhone.$touch()"
                data-test-id="alternate-phone-radio-group"
              >
                <v-radio
                  :ripple="false"
                  class="v-faux-radio mb-4"
                  label="Yes"
                  :value="true"
                  data-test-id="alternate-phone-yes"
                ></v-radio>

                <div class="expander" v-show="hasAlternatePhone">
                  <div
                    v-for="(phone, index) in alternatePhones"
                    :key="phone"
                    class="phone-block"
                    :data-test-id="'phone-block-' + index"
                  >
                    <div class="remove-area">
                      <span @click="removeAlternatePhone(index)" data-test-id="remove-phone">
                        <v-icon small :color="textColor"> fas fa-trash </v-icon>
                      </span>
                    </div>
                    <div class="faux-label" data-test-id="phone-type-label">Phone Type</div>
                    <div>
                      <v-select
                        v-model="phone.phoneType"
                        :items="phoneTypes"
                        :backgroundColor="inputBackgroundColor"
                        outlined
                        placeholder="Choose"
                        dense
                        hide-details="auto"
                        @change="
                          $v.alternatePhones.$each[index].phoneType.$touch()
                        "
                        :error-messages="phoneTypeErrors(index)"
                        class="mb-2"
                        :data-test-id="'phone-type-select-' + index"
                      >
                      </v-select>
                      <label for="phone" data-test-id="phone-number-label">Phone</label>
                      <v-text-field
                        outlined
                        type="text"
                        maxlength="256"
                        name="phone"
                        v-model.trim="phone.phoneNumber"
                        :backgroundColor="inputBackgroundColor"
                        @blur="
                          $v.alternatePhones.$each[index].phoneNumber.$touch()
                        "
                        :error-messages="phoneNumberErrors(index)"
                        hide-details="auto"
                        inputmode="numeric"
                        :data-test-id="'phone-number-input-' + index"
                      />
                    </div>
                  </div>
                  <a
                    v-if="alternatePhones.length < 3"
                    @click="addAlternatePhone"
                    data-test-id="add-phone-button"
                  >
                    <i class="fas fa-plus"></i> Add another alternate phone
                  </a>
                </div>
                <v-radio
                  :ripple="false"
                  class="v-faux-radio"
                  label="No"
                  :value="false"
                  data-test-id="alternate-phone-no"
                ></v-radio>
              </v-radio-group>
            </v-col>
          </v-row>

          <div class="skip-for-now">
            <a @click="skip()" data-test-id="skip-button">Skip for now</a>
          </div>
        </div>
      </v-container>
    </v-form>
    <bottom-nav v-on:go-prev="prev()" v-on:go-next="next()" data-test-id="alternate-phone-nav"></bottom-nav>
  </div>
</template>

<script>
import HelpDrawer from '@/components/HelpDrawerComponent';
import BottomNav from '@/components/common/BottomNav';
import DataSubmittedMessage from '@/components/client/DataSubmittedMessage';
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import { required, requiredIf } from 'vuelidate/lib/validators';
import {
  MENUS,
  ALTERNATE_PHONE_TYPES,
  formatPhone,
  validatePhoneLocal,
} from '@/common/constants';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';

// Custom phone type dup validator
const duplicateType = (phones) => (value) => {
  const typeCount = phones.filter((f) => f.phoneType === value);
  return typeCount && typeCount.length === 1;
};

// Custom phone number dup validator
// Security: Primary phone validation removed (PII not available)
const duplicatePhone = (phones) => (value) => {
  const phoneCount = phones.filter((f) => f.phoneNumber === value);
  return phoneCount && phoneCount.length === 1;
};

export default {
  name: 'AlternatePhone',

  components: { BottomNav, HelpDrawer, DataSubmittedMessage, SyncingIndicator },
  mixins: [InterviewClientMixin],

  data: () => ({
    hasAlternatePhone: null,
    alternatePhones: [
      {
        phoneType: null,
        phoneNumber: null,
      },
    ],
    MENUS: MENUS,
    ALTERNATE_PHONE_TYPES: ALTERNATE_PHONE_TYPES,
  }),

  validations() {
    return {
      hasAlternatePhone: {
        required,
      },
      alternatePhones: {
        $each: {
          phoneType: {
            required: requiredIf(() => this.hasAlternatePhone),
            dups: duplicateType(this.alternatePhones),
          },
          phoneNumber: {
            required: requiredIf(() => this.hasAlternatePhone),
            validatePhoneLocal,
            dups: duplicatePhone(this.alternatePhones),
          },
        },
      },
      validationGroup: ['hasAlternatePhone', 'alternatePhones'],
    };
  },

  computed: {
    helpTitle() {
      return 'Do I need another one?';
    },
    isSyncing() {
      const page = this.getPage(MENUS.CLIENT.ALTERNATE_PHONE.id);
      return page && page.status === 'IN_PROGRESS';
    },
  },

  methods: {
    /* Validatons for a phone instance type */
    phoneTypeErrors(idx) {
      const errors = [];
      if (!this.$v.alternatePhones.$each[idx].phoneType.$dirty) return errors;
      !this.$v.alternatePhones.$each[idx].phoneType.required &&
        errors.push('A selection is required');
      !this.$v.alternatePhones.$each[idx].phoneType.dups &&
        errors.push('Duplicate phone type');
      return errors;
    },

    /* Validatons for a phone instance number */
    phoneNumberErrors(idx) {
      const errors = [];
      if (!this.$v.alternatePhones.$each[idx].phoneNumber.$dirty) return errors;
      !this.$v.alternatePhones.$each[idx].phoneNumber.required &&
        errors.push('Phone Number is required');
      !this.$v.alternatePhones.$each[idx].phoneNumber.validatePhoneLocal &&
        errors.push('Invalid Phone');
      !this.$v.alternatePhones.$each[idx].phoneNumber.dups &&
        errors.push('Phone Number must be unique');
      return errors;
    },

    /* Add an Alternate phone instance */
    addAlternatePhone() {
      console.log(this.clientData);
      this.alternatePhones.push({
        phoneType: null,
        phoneNumber: null,
      });
    },

    /* Remove a specific Alternate phone instance */
    removeAlternatePhone(idx) {
      this.alternatePhones.splice(idx, 1);
    },

    /* Nav to Next Page in flow */
    next(route = null) {      
      const to = route || this.pages[this.pageIndex + 1];

      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      } else {
        this.goNext(to);
        return;
      }

      const data = {};
      if (this.hasAlternatePhone) {
        data.alternatePhones = [];
        this.alternatePhones.forEach((p) => {
          data.alternatePhones.push({
            type: p.phoneType,
            number: formatPhone(p.phoneNumber),
          });
        });
      }

      // Mark page as syncing immediately for better user feedback
      this.markPageAsSyncing(this.pages[this.pageIndex]);

      this.completeStep(this.pages[this.pageIndex], data)
        .then(() => {
          this.goNext(to);
        })
        .catch(() => {});
    },

    /* Nav to Previous Page in flow */
    prev() {
      this.goPrev(this.pages[this.pageIndex - 1]);
    },

    /* Skip to next Page in flow */
    skip() {      
      // Fetch updated interview progress before navigation
      this.$store.dispatch('GET_INTERVIEW_PROGRESS').finally(() => {
        this.goNext(this.pages[this.pageIndex + 1]);
      });
    },
  },
  created() {
    this.pages = this.interviewPages.getInterviewPages();
    this.pageIndex = this.pages.indexOf(MENUS.CLIENT.ALTERNATE_PHONE.id);
    // Initialize form data for current interview
    this.phoneTypes = ALTERNATE_PHONE_TYPES;
    this.formHidden = this.isAlternatePhoneComplete;
  },
};
</script>
<style lang="scss" scoped>
.expander {
  margin-bottom: 20px;

  .phone-block {
    padding-top: 10px;
    padding-bottom: 20px;
    border-top: 1px solid var(--secondary-color);
  }
  .remove-area {
    display: flex;
    justify-content: flex-end;
  }
}
.second-alternate {
  margin-top: 30px;
  padding-top: 15px;
  border-top: 1px solid var(--secondary-color);
}
</style>
